/**
 * Dogs API Client
 *
 * API client for dogs endpoints (CRUD operations)
 * Base URL: https://linkeddog-api.up.railway.app
 * Updated: 2025-01-22 - Aligned with API_GUIDE.md
 */

import { apiClient } from './client';
import type {
  Dog,
  DogsListResponse,
  CreateDogRequest,
  CreateDogResponse,
  UpdateDogRequest,
} from '@/shared/types/api.types';

export const dogsApi = {
  /**
   * Get all dogs for the authenticated user
   * Endpoint: GET /dogs/
   * Response: { data: Dog[] }
   */
  getAll: async (): Promise<Dog[]> => {
    const response = await apiClient.get<DogsListResponse>('/dogs/');
    return response.data.data;
  },

  /**
   * Get a single dog by ID
   * Endpoint: GET /dogs/{dogId}
   * Response: Dog
   */
  getById: async (dogId: string): Promise<Dog> => {
    const response = await apiClient.get<Dog>(`/dogs/${dogId}`);
    return response.data;
  },

  /**
   * Create a new dog
   * Endpoint: POST /dogs/
   * Response 201: { dogId: string }
   */
  create: async (data: CreateDogRequest): Promise<CreateDogResponse> => {
    const response = await apiClient.post<CreateDogResponse>('/dogs/', data);
    return response.data;
  },

  /**
   * Update an existing dog
   * Endpoint: PUT /dogs/{dogId}
   * Response 204: No Content
   */
  update: async (dogId: string, data: UpdateDogRequest): Promise<void> => {
    await apiClient.put(`/dogs/${dogId}`, data);
  },

  /**
   * Delete a dog (soft delete)
   * Endpoint: DELETE /dogs/{dogId}
   * Response 204: No Content
   */
  delete: async (dogId: string): Promise<void> => {
    await apiClient.delete(`/dogs/${dogId}`);
  },
};
